// dayjs.service.js
import dayjs from 'dayjs';
import 'dayjs/plugin/utc'; // Assume UTC is always needed
import 'dayjs/plugin/timezone'; // Assume timezone is always needed

class DayjsService {
  // Ensure all dependencies are correctly injected for minification
  static $inject = ["$q"];
  static timezone = 'America/New_York';

  /** @ngInject */
  constructor($q) {
    this.$q = $q;
    this.dayjs = dayjs;
    dayjs.extend(require('dayjs/plugin/utc'));
    dayjs.extend(require('dayjs/plugin/timezone'));
    this.durationPluginLoaded = false;
  }

  /**
   * Format a date/time in a given timezone.
   * @param {string|Date|number} input – Input date
   * @param {string} format – Output format string
   * @param {string} [tz] – IANA timezone (optional)
   * @returns {string}
   * this.formatted = this.DayjsService.format('2025-08-01T10:00:00Z', 'YYYY-MM-DD HH:mm', 'Asia/Kolkata');
   * this.relative  = this.DayjsService.fromNow('2025-08-01T10:00:00Z', 'Asia/Kolkata');
   * this.isFuture  = this.DayjsService.isAfter('2025-08-01', this.dayjs.now());
   * this.startDay  = this.DayjsService.startOf('2025-08-01T14:12:00', 'day').format();
   * this.endMonth  = this.DayjsService.endOf('2025-08-01', 'month').format();
   */
  format(input, format, tz) {
    if (!input) return '';
    let d = tz ? this.dayjs.tz(input, tz) : this.dayjs(input);
    return d.isValid() ? d.format(format) : '';
  }

  /**
   * Get the current date/time as a Dayjs object.
   * @param {string} [tz] Optional IANA timezone (e.g. 'America/New_York').
   * @returns {dayjs object}
   */
  now(tz) {
    return tz ? this.dayjs().tz(tz) : this.dayjs();
  }

  /**
   * Get the diff between two dates in the specified unit.
   */
  diff(a, b, unit = 'second') {
    return this.dayjs(a).diff(this.dayjs(b), unit);
  }

  /**
   * Add time to a date.
   */
  add(input, amount, unit) {
    return this.dayjs(input).add(amount, unit);
  }

  /**
   * Subtract time from a date.
   */
  subtract(input, amount, unit) {
    return this.dayjs(input).subtract(amount, unit);
  }

  /**
   * Parse a string/date/timestamp to Dayjs (with optional tz).
   */
  parse(input) {
    return this.dayjs.tz(input, this.timezone);
  }

  /**
   * Return ISO string in target timezone.
   */
  toISO(input, tz) {
    return this.format(input, undefined, tz);
  }

  /**
   * Relative time (e.g. "3 hours ago").
   */
  fromNow(input, tz) {
    let d = tz ? this.dayjs.tz(input, tz) : this.dayjs(input);
    return d.isValid() ? d.fromNow() : '';
  }

  // New method to lazily load duration
  async loadDurationPlugin() {
    if (this.durationPluginLoaded) {
      return this.$q.resolve();
    }
    try {
      // Dynamic import for the plugin file
      await import(/* webpackChunkName: "dayjs-duration-plugin" */ 'dayjs/plugin/duration');
      this.dayjs.extend(require('dayjs/plugin/duration'));
      this.durationPluginLoaded = true;
      return this.$q.resolve();
    } catch (error) {
      console.error('Failed to load Day.js duration plugin:', error);
      return this.$q.reject(error);
    }
  }

  // Method to get a duration object (call this only AFTER plugin is loaded)
  duration(ms) {
    if (!this.durationPluginLoaded) {
      console.warn("Day.js Duration plugin not loaded. Call loadDurationPlugin() first.");
      // Fallback or throw error
    }
    return this.dayjs.duration(ms);
  }

  /**
   * Is a before b?
   */
  isBefore(d, tz = this.timezone) {
    let a = this.now(tz);
    let b = this.dayjs.tz(d, tz);
    return a.isBefore(b);
  }

  /**
   * Is a after b?
   */
  isAfter(d, tz = this.timezone) {
    let a = this.now(tz);
    let b = this.dayjs.tz(d, tz);
    return a.isAfter(b);
  }

  /**
   * Start of unit (returns Dayjs object).
   */
  startOf(input, unit) {
    return this.dayjs(input).startOf(unit);
  }

  /**
   * End of unit (returns Dayjs object).
   */
  endOf(input, unit) {
    return this.dayjs(input).endOf(unit);
  }
}

export { DayjsService }
